import React, { ChangeEvent, useEffect } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import { FormField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { LossPercentageDto } from 'api/Clients/Types/LossPercentageDto'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { Modal } from 'components/CustomModal'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { isEmpty } from 'utils/is-empty'

interface DisclaimerLossPercentageModalProps {
  data?: LossPercentageDto
  visible: boolean
  onSubmit(values: LossPercentageValues): void
  onClose(): void
}

const useDisclaimerLossPercentageModal = () => {
  const dispatch = useAppDispatch()
  const identityApiClient = useApiClient(IdentityApiClient)
  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)

  useEffect(() => {
    if (isEmpty(tickmillCompanies)) {
      dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { tickmillCompanies }
}

export const DisclaimerLossPercentageAddModal: React.FC<DisclaimerLossPercentageModalProps> = ({
  data,
  visible,
  onSubmit,
  onClose,
}) => {
  const { tickmillCompanies } = useDisclaimerLossPercentageModal()

  return (
    <Modal width={600} visible={visible} onClose={onClose}>
      <DisclaimerLossPercentageForm
        isNew
        data={data}
        tickmillCompanies={tickmillCompanies}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </Modal>
  )
}

export const DisclaimerLossPercentageEditModal: React.FC<DisclaimerLossPercentageModalProps> = ({
  data,
  visible,
  onSubmit,
  onClose,
}) => {
  const { tickmillCompanies } = useDisclaimerLossPercentageModal()

  return (
    <Modal width={600} visible={visible} onClose={onClose}>
      <DisclaimerLossPercentageForm
        isNew={false}
        data={data}
        tickmillCompanies={tickmillCompanies}
        onSubmit={onSubmit}
        onClose={onClose}
      />
    </Modal>
  )
}

export const DisclaimerLossPercentageUI: React.FC<
  FormikProps<LossPercentageValues> & OuterProps
> = ({
  isNew,
  tickmillCompanies,
  isSubmitting,
  errors,
  handleSubmit,
  setSubmitting,
  setFieldValue,
  onClose,
}) => {
  const handleSubmitForm = (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <Form onSubmit={handleSubmitForm}>
      <DataGrid
        rightAddon={
          <ButtonGroup>
            <Button type='button' disabled={isSubmitting} variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={isSubmitting}>
              Save
            </Button>
          </ButtonGroup>
        }
      >
        <DataColumn>
          <DataCard title='Disclaimer Loss Percentage'>
            <FormSelectField
              name='tickmillCompanyId'
              label='Entity'
              options={tickmillCompanies}
              disabled={!isNew}
              error={errors.tickmillCompanyId}
              onChange={(value) => setFieldValue('tickmillCompanyId', value ? Number(value) : 0)}
            />
            <FormField
              name='percentage'
              label='Percentage'
              type='number'
              error={errors.percentage}
            />
          </DataCard>
        </DataColumn>
      </DataGrid>
    </Form>
  )
}

export interface LossPercentageValues {
  id?: string
  tickmillCompanyId: number
  percentage: number
}

interface OuterProps {
  isNew: boolean
  data?: LossPercentageDto
  tickmillCompanies: TickmillCompanyDto[]
  onSubmit(values: LossPercentageValues): void
  onClose(): void
}

const dataToSubmit = (values: LossPercentageValues): LossPercentageValues => {
  return values?.id
    ? {
        id: values.id,
        percentage: values.percentage,
        tickmillCompanyId: values.tickmillCompanyId,
      }
    : {
        percentage: values.percentage,
        tickmillCompanyId: values.tickmillCompanyId,
      }
}

export const DisclaimerLossPercentageForm = withFormik<OuterProps, LossPercentageValues>({
  mapPropsToValues: ({ data }) => {
    return {
      id: data?.id,
      tickmillCompanyId: data?.tickmillCompany?.id || 0,
      percentage: data?.percentage || 0,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<LossPercentageValues> = {}

    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'required'
    }
    if (values.percentage < 0) {
      errors.percentage = 'required'
    }

    return errors
  },
  enableReinitialize: true,
})(DisclaimerLossPercentageUI)
